/**
 * WgMeshStatus — live WireGuard mesh state for an LC.
 *
 * Polls `lookupWgPeer(lc.wg_ip)` and renders one of three terminal states
 * based on the server-side wg dump (NOT on `LocalComputer.setup_stage`,
 * which can stall at ENROLLED even when the peer is already in the mesh):
 *
 * - **A: peer NOT in mesh (404).** #48's auto-add daemon didn't apply;
 *   point at `/admin/host-infra` so the admin can verify the cron.
 * - **B: peer in mesh, handshake stale/zero.** Cron applied, jetson hasn't
 *   booted WG yet. Spinner + "run curl on the jetson" hint.
 * - **C: peer in mesh + recent handshake.** Green check, polling auto-stops.
 *
 * 503 (server's wg state transiently unavailable) renders as Case B.
 *
 * Reused by:
 *   - `LocalComputerDetailModal` — unbound LCs needing operator attention.
 *   - `SetupWizardPage` STAGE 1 — same shape applies to the wizard flow.
 *
 * No `t` prop — uses `useT()` internally so call sites outside the modal
 * don't have to thread the translator through.
 */
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Check, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import {
  lookupWgPeer,
  type WgPeerLookupResponse,
} from '@/api/localComputers';

interface Props {
  /**
   * Accepts any object exposing `wg_ip` — works with both the full
   * `LocalComputer` row and the smaller `LocalComputerSummary` shape.
   * When `wg_ip` is null the component renders nothing (caller should
   * also gate, but we're defensive here).
   */
  lc: { wg_ip: string | null };
  /**
   * Optional Case-B CTA. When provided, the "peer in mesh, no handshake"
   * state renders a "Показать curl" button that invokes this callback —
   * the operator's actual next action is to run curl on the jetson, so
   * we surface the curl source affordance directly. Set to undefined
   * (or omit) on surfaces that already show the curl elsewhere (e.g.
   * the LC detail modal renders the curl block right above the badge,
   * so no extra button is needed).
   */
  onShowCurl?: () => void;
  /**
   * Suppress rendering for Case B ("peer in mesh, no handshake") +
   * loading + 503 fallback. The LC detail modal sets this because its
   * curl card directly above already conveys the "run this on the
   * jetson" CTA — duplicating it as a separate panel is clutter
   * (per Alexey 2026-05-21). Case A (peer missing) still renders.
   * Wizard surfaces don't embed the curl card, so they leave this false.
   */
  hideCaseB?: boolean;
  /**
   * Suppress rendering for Case C ("peer in mesh, recent handshake").
   * The modal sets this because the stage checklist's
   * `WG_HANDSHAKE_VERIFIED` row already carries the same signal —
   * a standalone success panel below is just clutter once the
   * operator can see the ✓ in the checklist (per Alexey 2026-05-22).
   * Wizards / standalone surfaces leave this false so the timestamp
   * is visible.
   */
  hideCaseC?: boolean;
}

// Poll cadence — bumped from 5s to 30s per Alexey 2026-05-21. The badge
// flipping a few seconds later when the operator does kick wg-quick is
// fine; second-by-second polling hammered the BE for no UX gain.
const POLL_INTERVAL_MS = 30_000;

export default function WgMeshStatus({
  lc,
  onShowCurl,
  hideCaseB = false,
  hideCaseC = false,
}: Props) {
  const { t } = useT();
  const navigate = useNavigate();

  // SWR with a null key short-circuits the fetch — safe when `wg_ip` is
  // missing while keeping the hook call unconditional (rules-of-hooks).
  const { data, error } = useSWR<
    WgPeerLookupResponse,
    { response?: { status?: number } }
  >(
    lc.wg_ip ? `wg-peer-lookup:${lc.wg_ip}` : null,
    () => lookupWgPeer(lc.wg_ip!).then(r => r.data),
    {
      // Stop polling once we've seen a recent handshake. SWR returns 0
      // from refreshInterval to disable the next tick (same pattern as
      // useDiscoveryRun).
      refreshInterval: latest =>
        latest?.has_handshake_recently ? 0 : POLL_INTERVAL_MS,
      shouldRetryOnError: false,
      revalidateOnFocus: true,
    },
  );

  if (!lc.wg_ip) return null;

  const notFound = error?.response?.status === 404;

  // Case A — peer missing from wg0.conf. #48's auto-add daemon didn't
  // apply; point at host-infra so the admin can verify the cron.
  if (notFound) {
    return (
      <div className="space-y-2 rounded-md border border-destructive/40 bg-destructive/10 p-3">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-destructive">
          <AlertTriangle className="size-3.5" />
          {t('admin.lc.detail.wgPeerMissingHeading')}
        </div>
        <p className="text-xs text-muted-foreground">
          {t('admin.lc.detail.wgPeerMissingHint')}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/admin/host-infra')}
        >
          {t('admin.lc.detail.wgPeerMissingCta')}
        </Button>
      </div>
    );
  }

  // Case C — peer present + recent handshake. Job done for this layer.
  // Suppressed in modal context (`hideCaseC`) where the
  // SetupStageChecklist's WG_HANDSHAKE_VERIFIED row already carries
  // the same ✓ signal.
  if (data?.has_handshake_recently) {
    if (hideCaseC) return null;
    return (
      <div className="space-y-1 rounded-md border border-emerald-300 bg-emerald-50/50 p-3 dark:border-emerald-700 dark:bg-emerald-950/20">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
          <Check className="size-3.5" />
          {t('admin.lc.detail.wgPeerConnected', {
            ago: data.latest_handshake
              ? formatRelativeAgo(data.latest_handshake, t)
              : '—',
          })}
        </div>
      </div>
    );
  }

  // Case B (and the initial-fetch / 503-transient fallback) — peer
  // enqueued but jetson hasn't completed handshake yet. Styled as a
  // **next-step info panel** (sky/blue tint, matching the tty-hint
  // banner from #58), NOT a warning — this is the normal-flow state
  // where the operator-action is pending. Genuine warnings (Case A:
  // peer missing) stay amber/destructive.
  //
  // Suppressed on surfaces that already show the curl card inline
  // (modal): `hideCaseB` collapses to null because the curl card
  // above already conveys "run this on the jetson".
  if (hideCaseB) return null;
  return (
    <div className="space-y-2 rounded-md border border-sky-200 bg-sky-50/60 p-3 text-sky-800 dark:border-sky-800 dark:bg-sky-950/20 dark:text-sky-200">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide">
        <Info className="size-3.5" />
        {t('admin.lc.detail.wgPeerWaiting')}
      </div>
      <p className="text-xs">
        {t('admin.lc.detail.wgPeerWaitingHint')}
      </p>
      {onShowCurl && (
        <Button variant="outline" size="sm" onClick={onShowCurl}>
          {t('admin.lc.detail.wgPeerShowCurl')}
        </Button>
      )}
    </div>
  );
}

function formatRelativeAgo(iso: string, t: (k: string) => string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.max(0, Math.round(diff / 1000));
  if (s < 60) return `${s}s ${t('admin.lc.detail.ago')}`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ${t('admin.lc.detail.ago')}`;
  const h = Math.round(m / 60);
  return `${h}h ${t('admin.lc.detail.ago')}`;
}
